import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO, { breadcrumbJsonLd } from '../../components/SEO';
import { LITE_SIGNUP_URL } from '../../data/links';
import { useDisplayCurrency } from '../../hooks/useDisplayCurrency';
import CurrencySelect from './CurrencySelect';
import { formatMoney } from '../../lib/currency';
import {
  BILLING_CYCLES,
  LITE_EXTRA_DRIVER_USD,
  LITE_EXTRA_ORDER_USD,
  LITE_INCLUDED_ORDERS,
  LITE_PLAN_KEYS,
  LITE_PRICE_BY_CYCLE,
  LITE_STAFF_SEATS,
  PLAN_INCLUDED_DRIVERS,
  liteMaxSavingPercent,
  litePeriodTotalUsd,
  liteSavingPercent,
  liteSignupUrl,
  type BillingCycle,
  type LitePlanKey,
} from './pricingDisplay';

const LITE_PLANS = LITE_PLAN_KEYS;
const FEATURED_PLAN: LitePlanKey = 'litePlus';
const PLAN_ICONS: Record<LitePlanKey, string> = {
  lite: 'ri-route-line',
  litePlus: 'ri-plug-line',
  liteAutopilot: 'ri-steering-2-line',
};
const PLAN_NAMES: Record<LitePlanKey, string> = {
  lite: 'Geofleet Lite',
  litePlus: 'Geofleet Lite Plus',
  liteAutopilot: 'Geofleet Lite Autopilot',
};

export default function LitePricingPage() {
  const { t, i18n } = useTranslation();
  const { currency, setCurrency, rateLine } = useDisplayCurrency();
  const [cycle, setCycle] = useState<BillingCycle>('annual');

  const priceFor = (key: LitePlanKey) => formatMoney(LITE_PRICE_BY_CYCLE[key][cycle], currency);

  const billedLine = (key: LitePlanKey) => {
    if (cycle === 'monthly') return t('pricing.lite.billing.billedMonthly');
    const amount = formatMoney(litePeriodTotalUsd(key, cycle), currency);
    return t(cycle === 'quarterly' ? 'pricing.lite.billing.billedQuarterly' : 'pricing.lite.billing.billedAnnual', { amount });
  };

  const seatsFor = (key: LitePlanKey) => {
    const seats = LITE_STAFF_SEATS[key];
    return seats === null ? t('pricing.lite.extras.unlimited') : String(seats);
  };

  const notIncludedFor = (key: LitePlanKey) => {
    const raw = t(`pricing.plans.${key}.notIncluded`, { returnObjects: true, lng: i18n.language });
    return Array.isArray(raw) ? (raw as string[]) : [];
  };

  const featuresFor = (key: LitePlanKey) => {
    const raw = t(`pricing.plans.${key}.features`, { returnObjects: true, lng: i18n.language });
    return Array.isArray(raw) ? (raw as string[]) : [];
  };

  const jsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Geofleet Lite',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, iOS, Android',
    url: 'https://geofleet.ai/pricing/lite',
    offers: LITE_PLANS.map((key) => ({
      '@type': 'Offer',
      name: PLAN_NAMES[key],
      price: String(LITE_PRICE_BY_CYCLE[key].monthly),
      priceCurrency: 'USD',
      description: `${PLAN_INCLUDED_DRIVERS[key]} drivers and ${LITE_INCLUDED_ORDERS[key]} orders per month from one hub. ${LITE_PRICE_BY_CYCLE[key].quarterly} USD/month billed quarterly, ${LITE_PRICE_BY_CYCLE[key].annual} USD/month billed annually.`,
      priceSpecification: [
        { '@type': 'UnitPriceSpecification', price: String(LITE_PRICE_BY_CYCLE[key].monthly), priceCurrency: 'USD', billingDuration: 'P1M' },
        { '@type': 'UnitPriceSpecification', price: String(litePeriodTotalUsd(key, 'quarterly')), priceCurrency: 'USD', billingDuration: 'P3M' },
        { '@type': 'UnitPriceSpecification', price: String(litePeriodTotalUsd(key, 'annual')), priceCurrency: 'USD', billingDuration: 'P1Y' },
      ],
    })),
  }), []);

  return (
    <div className="pt-[60px]">
      <SEO
        title="Geofleet Lite Pricing"
        description="Three flat plans for single-hub delivery teams. Geofleet Lite starts at $49/month for 3 drivers and 500 orders, or $39/month billed annually. Save on quarterly and annual billing."
        jsonLd={[
          breadcrumbJsonLd([
            { name: 'Home', url: 'https://geofleet.ai' },
            { name: 'Pricing', url: 'https://geofleet.ai/pricing' },
            { name: 'Geofleet Lite', url: 'https://geofleet.ai/pricing/lite' },
          ]),
          jsonLd,
        ]}
      />

      <section className="relative isolate overflow-hidden bg-[#f4f8ff]">
        <div className="pointer-events-none absolute inset-0" aria-hidden="true">
          <div className="absolute -right-28 -top-36 h-[500px] w-[500px] rounded-full bg-blue-300/30 blur-[100px]" />
          <div className="absolute -bottom-48 left-[-8%] h-[420px] w-[620px] rounded-full bg-cyan-200/35 blur-[100px]" />
          <div className="absolute inset-0 opacity-40 [background-image:linear-gradient(rgba(23,105,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(23,105,255,0.05)_1px,transparent_1px)] [background-size:44px_44px] [mask-image:linear-gradient(to_bottom,black,transparent_90%)]" />
        </div>
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-6 py-14 lg:grid-cols-[1.05fr_.95fr] lg:gap-16 lg:py-20">
          <div className="text-center lg:text-start">
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-white/75 px-3.5 py-1.5 text-xs font-bold text-blue-800 shadow-sm">
              <i className="ri-store-2-line" aria-hidden />
              {t('pricing.lite.eyebrow')}
            </span>
            <div className="mx-auto mt-6 inline-flex rounded-xl border border-blue-200/80 bg-white/75 p-1 shadow-sm lg:mx-0">
              <span className="rounded-lg bg-[#1769ff] px-4 py-2 text-sm font-semibold text-white" aria-current="page">{t('pricing.lite.eyebrow')}</span>
              <Link to="/pricing" className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-blue-50 hover:text-slate-950">{t('pricing.platform.eyebrow')}</Link>
            </div>
            <h1 className="mx-auto mt-6 max-w-2xl text-4xl font-bold leading-[1.06] tracking-[-0.04em] text-slate-950 sm:text-5xl lg:mx-0 lg:text-6xl">{t('pricing.lite.title')}</h1>
            <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-slate-600 lg:mx-0">{t('pricing.lite.subtitle')}</p>
            <div className="mt-7 flex flex-col items-center gap-3 lg:items-start">
              <CurrencySelect currency={currency} onChange={setCurrency} />
              {rateLine ? <p className="max-w-xl text-sm text-slate-500">{t('pricing.currencyNote', { rate: rateLine })}</p> : null}
            </div>
          </div>

          <div className="relative mx-auto w-full max-w-[540px]" aria-hidden="true">
            <div className="absolute inset-x-[14%] top-[12%] h-[75%] rounded-full bg-blue-300/45 blur-[65px]" />
            <div className="relative rounded-[28px] border border-white/90 bg-white/85 p-5 shadow-[0_28px_90px_rgba(35,83,150,0.17)] backdrop-blur-xl sm:p-7">
              <div className="flex items-center justify-between gap-4 border-b border-slate-100 pb-5">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-700">{t('pricing.plans.lite.name')}</p>
                  <p className="mt-1 text-sm text-slate-500">{t('pricing.plans.lite.tagline')}</p>
                </div>
                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-xl text-[#1769ff]"><i className="ri-route-line" /></span>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-[#f5f8ff] p-4">
                  <p className="text-2xl font-bold tracking-tight text-slate-950">{PLAN_INCLUDED_DRIVERS.lite}</p>
                  <p className="mt-1 text-xs font-medium text-slate-500">{t('pricing.unified.columns.drivers')}</p>
                </div>
                <div className="rounded-2xl bg-[#f5f8ff] p-4">
                  <p className="text-2xl font-bold tracking-tight text-slate-950">{LITE_INCLUDED_ORDERS.lite}</p>
                  <p className="mt-1 text-xs font-medium text-slate-500">{t('pricing.unified.columns.orders')}</p>
                </div>
              </div>
              <div className="relative mt-5 overflow-hidden rounded-2xl bg-[#0b1826] p-5 text-white">
                <div className="absolute -right-8 -top-12 h-32 w-32 rounded-full border border-white/10" />
                <div className="relative flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold text-blue-200">{t('pricing.lite.eyebrow')}</p>
                    <p className="mt-1 text-3xl font-bold tracking-tight">{priceFor('lite')}<span className="ms-1 text-sm font-medium text-slate-300">{t('pricing.plans.lite.period')}</span></p>
                    <p className="mt-1 text-xs text-slate-400">{billedLine('lite')}</p>
                  </div>
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500 text-lg"><i className="ri-check-line" /></span>
                </div>
                <div className="relative mt-5 flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-emerald-400" />
                  <span className="text-xs text-slate-300">{t('pricing.plans.lite.description')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24" aria-labelledby="lite-pricing-title">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">{t('pricing.lite.eyebrow')}</p>
            <h2 id="lite-pricing-title" className="mt-3 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">{t('litePage.pricingTitle')}</h2>
            <p className="mt-4 leading-relaxed text-slate-600">{t('litePage.pricingIntro')}</p>
          </div>

          <div className="mt-8 flex justify-center">
            <div role="radiogroup" aria-label={t('pricing.lite.billing.label')} className="inline-flex flex-wrap justify-center gap-1 rounded-2xl border border-slate-200 bg-slate-50 p-1">
              {BILLING_CYCLES.map((option) => {
                const active = option === cycle;
                const saving = option === 'monthly' ? 0 : liteMaxSavingPercent(option);
                return (
                  <button
                    key={option}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setCycle(option)}
                    className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${active ? 'bg-white text-slate-950 shadow-sm ring-1 ring-slate-200' : 'text-slate-600 hover:text-slate-950'}`}
                  >
                    {t(`pricing.lite.billing.${option}`)}
                    {saving > 0 ? (
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${active ? 'bg-emerald-100 text-emerald-800' : 'bg-emerald-50 text-emerald-700'}`}>
                        {t('pricing.lite.billing.saveUpTo', { percent: saving })}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>

          <p className="mt-4 flex items-center justify-center gap-2 text-sm font-medium text-slate-700">
            <i className="ri-gift-line text-base text-emerald-600" aria-hidden />
            {t('pricing.lite.trial')}
          </p>

          <div className="mx-auto mt-8 grid max-w-6xl gap-5 lg:grid-cols-3">
            {LITE_PLANS.map((key) => {
              const featured = key === FEATURED_PLAN;
              const saving = liteSavingPercent(key, cycle);
              return (
                <article key={key} className={`relative flex flex-col overflow-hidden rounded-3xl border p-6 sm:p-8 ${featured ? 'border-blue-300 bg-[#f5f8ff] shadow-[0_20px_60px_rgba(23,105,255,0.12)]' : 'border-slate-200 bg-white shadow-[0_18px_50px_rgba(15,23,42,0.06)]'}`}>
                  {featured ? <span className="absolute end-6 top-6 rounded-full bg-[#1769ff] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">{t('pricing.plans.litePlus.highlight')}</span> : null}
                  <p className="flex items-center gap-2 text-sm font-bold text-blue-700">
                    <i className={`${PLAN_ICONS[key]} text-base`} aria-hidden />
                    {t(`pricing.plans.${key}.badge`)}
                  </p>
                  <h3 className="mt-2 text-2xl font-bold tracking-tight text-slate-950">{t(`pricing.plans.${key}.name`)}</h3>
                  <p className="mt-2 min-h-12 max-w-sm text-sm leading-relaxed text-slate-600">{t(`pricing.plans.${key}.tagline`)}</p>
                  <p className="mt-6 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <span className="text-5xl font-bold tracking-[-0.045em] text-slate-950">{priceFor(key)}</span>
                    <span className="text-sm font-medium text-slate-500">{t(`pricing.plans.${key}.period`)}</span>
                    {saving > 0 ? (
                      <span className="ms-1 text-sm font-medium text-slate-400 line-through">{formatMoney(LITE_PRICE_BY_CYCLE[key].monthly, currency)}</span>
                    ) : null}
                  </p>
                  <p className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-600">
                    <span>{billedLine(key)}</span>
                    {saving > 0 ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-bold text-emerald-700">{t('pricing.lite.billing.save', { percent: saving })}</span> : null}
                  </p>
                  <p className="mt-3 text-sm text-slate-600">{t(`pricing.plans.${key}.description`)}</p>

                  <dl className="mt-5 grid grid-cols-1 gap-2 rounded-2xl border border-slate-200/80 bg-white/70 p-4 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-slate-500">{t('pricing.lite.extras.hubs')}</dt>
                      <dd className="font-semibold text-slate-900">{t('pricing.lite.extras.oneHub')}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-slate-500">{t('pricing.lite.extras.extraDriver')}</dt>
                      <dd className="font-semibold text-slate-900">{t('pricing.lite.extras.perDriverMonth', { amount: formatMoney(LITE_EXTRA_DRIVER_USD[key], currency) })}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-slate-500">{t('pricing.lite.extras.extraOrder')}</dt>
                      <dd className="font-semibold text-slate-900">{t('pricing.lite.extras.perOrder', { amount: formatMoney(LITE_EXTRA_ORDER_USD[key], currency) })}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-slate-500">{t('pricing.lite.extras.staffSeats')}</dt>
                      <dd className="font-semibold text-slate-900">{seatsFor(key)}</dd>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <dt className="text-slate-500">{t('pricing.lite.extras.autopilot')}</dt>
                      <dd className="font-semibold text-slate-900">{t(`pricing.plans.${key}.autopilotMode`)}</dd>
                    </div>
                  </dl>

                  <ul className="m-0 mt-6 flex-1 list-none space-y-3 border-t border-slate-200 pt-6">
                    {featuresFor(key).map((feature) => (
                      <li key={feature} className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-700">
                        <i className="ri-check-line mt-0.5 text-base font-bold text-[#1769ff]" aria-hidden />
                        <span>{feature}</span>
                      </li>
                    ))}
                    {notIncludedFor(key).length > 0 ? (
                      <li className="pt-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{t('pricing.lite.extras.notIncludedLabel')}</li>
                    ) : null}
                    {notIncludedFor(key).map((feature) => (
                      <li key={`no-${feature}`} className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-400">
                        <i className="ri-close-line mt-0.5 text-base" aria-hidden />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <a href={liteSignupUrl(LITE_SIGNUP_URL, key, cycle)} target="_blank" rel="noopener noreferrer" className={`mt-8 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3.5 text-sm font-semibold transition-all hover:-translate-y-0.5 ${featured ? 'bg-[#1769ff] text-white shadow-lg shadow-blue-600/20 hover:bg-blue-700' : 'bg-slate-950 text-white hover:bg-slate-800'}`}>
                    {t(`pricing.plans.${key}.cta`)}<i className="ri-arrow-right-line rtl:rotate-180" aria-hidden />
                  </a>
                </article>
              );
            })}
          </div>

          <p className="mx-auto mt-6 max-w-3xl text-center text-xs leading-relaxed text-slate-500">{t('pricing.lite.footnote')}</p>

        </div>
      </section>
    </div>
  );
}
